import { AnyVariables, CombinedError, createClient, fetchExchange, OperationContext, OperationResult } from "urql";
import { Config } from "./Config";
import { DocumentNode } from "graphql";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { redirectToSignin } from "appHistory";
import { nonEmpty } from "./helpers/coll";
import { sleep } from "./helpers/sleep";
import { buffer, interval, onEnd, pipe, subscribe, Subscription } from "wonka";
import { useRef, useState } from "react";

const gqlClient = createClient({
  url: Config.backendBase + "/graphql/api",
  requestPolicy: "network-only",
  fetchSubscriptions: true,
  fetchOptions: () => {
    return {
      credentials: "include",
      headers: {
        // We override the default here to remove text/event-stream since it gets used
        // for all queries and mutations instead of JSON if enabled.
        // We only want it enabled for subscriptions.
        accept: "application/graphql-response+json, application/graphql+json, application/json",
      },
    };
  },
  exchanges: [fetchExchange],
});

export async function queryOrFail<T, V extends AnyVariables>(
  response: Promise<OperationResult<T, V>>,
  noRedirect = false
): Promise<T> {
  const result = await response;
  if (result.error !== undefined) {
    const response = result.error.response;
    if (response !== undefined && response !== null) {
      if (response.status === 401 && !noRedirect) {
        redirectToSignin();
        return sleep(10000).then(() => Promise.reject("Redirecting to signin page"));
      } else {
        if (result.error.message === "[Network] No Content") {
          const err = new Error(`Request failed with code ${response.status}`);
          err["status"] = response.status;
          return Promise.reject(err);
        } else {
          return Promise.reject(result.error);
        }
      }
    } else {
      if (nonEmpty(result.error.graphQLErrors)) {
        const message = result.error.graphQLErrors[0]?.message;
        return Promise.reject(message);
      } else {
        return Promise.reject(result.error);
      }
    }
  } else {
    return Promise.resolve(result.data!);
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function runGqlQuery<Data = any, Variables extends AnyVariables = AnyVariables>(
  query: DocumentNode | TypedDocumentNode<Data, Variables> | string,
  variables: Variables,
  noRedirect = false
): Promise<Data> {
  return queryOrFail(gqlClient.query(query, variables).toPromise(), noRedirect);
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function runGqlMutation<Data = any, Variables extends { [key: string]: any } = {}>(
  mutation: DocumentNode | TypedDocumentNode<Data, Variables> | string,
  variables: Variables
): Promise<Data> {
  return queryOrFail(gqlClient.mutation(mutation, variables).toPromise());
}

// For subscriptions, we tell the server that we accept text/event-stream, and only text/event-stream
const subscriptionContext: Partial<OperationContext> = {
  fetchSubscriptions: true,
  fetchOptions: {
    credentials: "include",
    headers: {
      accept: "text/event-stream",
    },
  },
};

export interface SubscriptionGqlError {
  kind: "graphqlError";
  error: CombinedError;
}

export interface SubscriptionHandlerError {
  kind: "handlerError";
  error: Error;
}

export interface SubscriptionSuccess<Data, Variables extends AnyVariables = AnyVariables> {
  kind: "success";
  data: Data;
  fullResult: OperationResult<Data, Variables>;
}
export interface SubscriptionCancel {
  kind: "cancel";
}

export interface SubscriptionComplete {
  kind: "complete";
}

export type GqlSubscriptionMessage<Data, Variables extends AnyVariables = AnyVariables> =
  | SubscriptionGqlError
  | SubscriptionHandlerError
  | SubscriptionSuccess<Data, Variables>
  | SubscriptionCancel
  | SubscriptionComplete;

export function isGqlError<Data>(msg: GqlSubscriptionMessage<Data>): msg is SubscriptionGqlError {
  return msg.kind === "graphqlError";
}

export function isSuccess<Data, Variables extends AnyVariables>(
  msg: GqlSubscriptionMessage<Data, Variables>
): msg is SubscriptionSuccess<Data, Variables> {
  return msg.kind === "success";
}

export function isComplete<Data>(msg: GqlSubscriptionMessage<Data>): msg is SubscriptionComplete {
  return msg.kind === "complete";
}

export function isCancel<Data>(msg: GqlSubscriptionMessage<Data>): msg is SubscriptionCancel {
  return msg.kind === "cancel";
}

export type GqlSubscriptionOptions = {
  // Buffer messages that arrive in the given ms timeframe so we don't render too often
  bufferIntervalMs?: number;
};

export function runGqlSubscription<Data = any, Variables extends AnyVariables = AnyVariables>(
  subscription: DocumentNode | TypedDocumentNode<Data, Variables> | string,
  variables: Variables,
  handler: (msgBuffer: GqlSubscriptionMessage<Data, Variables>[]) => void,
  options?: GqlSubscriptionOptions
): Subscription {
  const notifier = interval(options?.bufferIntervalMs || 16);
  return pipe(
    gqlClient.subscription(subscription, variables, subscriptionContext),
    buffer(notifier),
    onEnd(() => {
      // Not always a cancel in this layer, but we handle that in the hook
      handler([{ kind: "cancel" }]);
    }),
    subscribe((operationResults) => {
      const handlerPayloads: GqlSubscriptionMessage<Data, Variables>[] = operationResults.map((or) => {
        if (or.hasNext) {
          if (or.error) {
            return { kind: "graphqlError", error: or.error };
          } else {
            const data = or.data!;
            return { kind: "success", data, fullResult: or };
          }
          // The final message always has hasNext = false, and is a duplicate of the previous payload
          // I think its just a byproduct of how the eventStream protocol produces an "event complete" message
        } else {
          return { kind: "complete" };
        }
      });
      handler(handlerPayloads);
    })
  );
}

export function useGqlSubscription<Data = any, Variables extends AnyVariables = AnyVariables>(
  subscription: DocumentNode | TypedDocumentNode<Data, Variables> | string,
  variables: Variables,
  handler: (msgBuffer: GqlSubscriptionMessage<Data, Variables>[]) => void,
  options?: GqlSubscriptionOptions
): { cancel: () => void; inProgress: boolean; subscribe: () => void } {
  const inProgressRef = useRef<boolean>(false);
  const subscriptionRef = useRef<Subscription>();
  const [, setForceRender] = useState(0);

  const runHandlerSafe = (msgBuffer: GqlSubscriptionMessage<Data, Variables>[]) => {
    try {
      handler(msgBuffer);
    } catch (e: any) {
      handler([{ kind: "handlerError", error: new Error(`Handler threw: ${e.toString()}`) }]);
    }
  };

  const subscribe = () => {
    inProgressRef.current = true;
    const sub = runGqlSubscription(
      subscription,
      variables,
      (msgBuffer) => {
        const messagesToRun: GqlSubscriptionMessage<Data, Variables>[] = [];
        for (const msg of msgBuffer) {
          if (msg.kind === "success") {
            messagesToRun.push(msg);
          } else if (msg.kind === "graphqlError") {
            subscriptionRef.current = undefined;
            inProgressRef.current = false;
            messagesToRun.push(msg);
          } else if (msg.kind === "cancel" || msg.kind === "complete") {
            setForceRender((prev) => prev + 1);
            // got 'done' while in progress, this is either a cancel or a completion
            if (inProgressRef.current) {
              inProgressRef.current = false;
              subscriptionRef.current = undefined;
              messagesToRun.push(msg);
            } else {
              // got 'done' while not in progress, this is just the followup 'done';
              // Do not call the handler callback since they already think the stream is done
              inProgressRef.current = false;
              subscriptionRef.current = undefined;
            }
          }
        }
        runHandlerSafe(messagesToRun);
      },
      options
    );
    subscriptionRef.current = sub;
  };

  const cancel = () => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      inProgressRef.current = false;
      subscriptionRef.current = undefined;
      setForceRender((prev) => prev + 1);
    }
  };

  return {
    subscribe,
    cancel,
    inProgress: inProgressRef.current,
  };
}

export default gqlClient;
